import type { ReactNode } from 'react';
import { SystemLoggingEvent } from '../../../types/SystemLogging.types';
import { formatEventDate } from '../../../utils/EventFormatting.util';
import { getEventUserDisplay } from '../../../utils/Functions.utils';
import { AttentionMessage } from '../../ui/AttentionMessage/AttentionMessage';
import { ChangeImpactBadge } from '../../ui/ChangeImpactBadge/ChangeImpactBadge';

type SideDrawerItemProps = {
  children: ReactNode;
  label: string;
  className?: string;
};

function SideDrawerItem({
  children,
  label,
  className = 'flex flex-col gap-2'
}: SideDrawerItemProps) {
  return (
    <div className={className}>
      <div className="text-xs leading-5 text-gray-550 font-medium uppercase">
        {label}:
      </div>
      {children}
    </div>
  );
}

export function getEventDetailsDrawerContent(
  selectedEvent: SystemLoggingEvent
): ReactNode[] {
  const { name: userDisplayName, initials: userInitials } =
    getEventUserDisplay(selectedEvent);

  return [
    <SideDrawerItem key="severity" label="severity">
      <ChangeImpactBadge impact={selectedEvent.changeImpact} />
    </SideDrawerItem>,

    <SideDrawerItem key="description" label="description">
      <p className="w-full whitespace-normal break-words text-sm text-zinc-900">
        {selectedEvent.message}
      </p>
    </SideDrawerItem>,

    <div className="flex flex-col gap-4">
      <SideDrawerItem label="dataset">
        <p className="text-sm text-zinc-900 mb-2">
          {selectedEvent.repositoryName}
        </p>
        <div className="flex gap-2">
          <SideDrawerItem label="user" className="flex flex-col gap-2 w-1/2">
            <div className="flex gap-2 items-center">
              <span className="w-6 h-6 rounded-xl bg-[#EEEEF0] flex items-center justify-center">
                <span className="font-inter text-center font-normal text-xs leading-3 text-[#4E4E56]">
                  {userInitials}
                </span>
              </span>
              <span className="text-left text-sm text-gray-550">
                {userDisplayName}
              </span>
            </div>
          </SideDrawerItem>
          <SideDrawerItem
            label="date"
            className="flex flex-col gap-2 w-1/2 justify-between"
          >
            <p className="text-sm text-gray-550">
              {formatEventDate(selectedEvent.createdAt)}
            </p>
          </SideDrawerItem>
        </div>
      </SideDrawerItem>
    </div>,

    ...(selectedEvent.changeImpact === 'MAJOR'
      ? [
          <div key="suggested-actions" className="flex flex-col gap-2">
            <AttentionMessage />
          </div>
        ]
      : [])
  ];
}
